/**
 * Admin page for on-chain campaigns (specification §5.5, §6.1). It lists the deposits the owner
 * must void, which an admin signed in with the owner wallet voids from here, and every campaign
 * Morcast has read from the chain.
 */

import { CircleCheck, Megaphone } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { useConnection } from "wagmi";

import { type AdminCampaign, useAdminCampaigns, useVoidDeposit } from "../../admin/campaigns.js";
import { useSession } from "../../auth/session.js";
import type { Campaign } from "../../campaigns/requests.js";
import { AddressLink, shortHex, TransactionLink } from "../../components/ChainLinks.js";
import { ConfirmDialog } from "../../components/ConfirmDialog.js";
import { RequireAdmin } from "../../components/RequireAdmin.js";
import { SignInDialog } from "../../components/SignInDialog.js";
import {
  Badge,
  EmptyState,
  ErrorMessage,
  Fact,
  Loading,
  Notice,
  PageHeader,
  styles,
} from "../../components/ui.js";
import { formatAmount, formatUtc } from "../../format.js";
import { describeEscrowError } from "../../wallet/escrow.js";

export function AdminCampaigns() {
  return (
    <RequireAdmin>
      <CampaignsPage />
    </RequireAdmin>
  );
}

function CampaignsPage() {
  const toVoid = useAdminCampaigns(true);
  const all = useAdminCampaigns(false);

  return (
    <section>
      <PageHeader eyebrow="Admin" title="Campaigns" documentTitle="Campaigns · Admin">
        Every deposit Morcast has read from the chain. A deposit that funds no accepted request is
        voided from the owner wallet, which returns its whole budget to its brand.
      </PageHeader>

      <section aria-labelledby="to-void-title" className="mt-10">
        <h2 id="to-void-title" className="text-xl font-semibold tracking-tight">
          Deposits to void
        </h2>
        <p className="mt-2 max-w-3xl text-sm text-zinc-600 dark:text-zinc-400">
          Sign in with the owner wallet to void each deposit here. The call is shown too, for any
          other wallet tool. Once the worker reads the escrow's event, the deposit leaves this list.
        </p>
        <div className="mt-4">
          {toVoid.isPending && <Loading />}
          {toVoid.isError && <ErrorMessage error={toVoid.error} />}
          {toVoid.data?.length === 0 && (
            <EmptyState icon={CircleCheck} title="No deposit needs voiding." />
          )}
          {toVoid.data && toVoid.data.length > 0 && (
            <ul className="space-y-4">
              {toVoid.data.map((campaign) => (
                <VoidCard key={`${campaign.escrow}-${campaign.id}`} campaign={campaign} />
              ))}
            </ul>
          )}
        </div>
      </section>

      <section aria-labelledby="all-title" className="mt-12">
        <h2 id="all-title" className="text-xl font-semibold tracking-tight">
          All campaigns
        </h2>
        <div className="mt-4">
          {all.isPending && <Loading />}
          {all.isError && <ErrorMessage error={all.error} />}
          {all.data?.length === 0 && <EmptyState icon={Megaphone} title="No campaign yet." />}
          {all.data && all.data.length > 0 && <CampaignTable campaigns={all.data} />}
        </div>
      </section>
    </section>
  );
}

/** A deposit to void, with why it funds nothing and the owner's call. */
function VoidCard({ campaign }: { campaign: AdminCampaign }) {
  return (
    <li className={styles.card}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">Campaign {campaign.id}</span>
        <span className="font-mono text-xs text-zinc-500 dark:text-zinc-400">
          {shortHex(campaign.escrow)}
        </span>
        <CampaignStatusBadge status={campaign.status} />
      </div>
      <p className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">{campaign.unmatchedReason}</p>
      <dl className="mt-4 grid gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
        <Fact label="Brand">
          <AddressLink chainId={campaign.chainId} address={campaign.brand} />
        </Fact>
        <Fact label="Budget">{formatAmount(campaign.budget, campaign.token)}</Fact>
        <Fact label="Deposited">
          {formatUtc(campaign.createdAt)} in{" "}
          <TransactionLink chainId={campaign.chainId} hash={campaign.createdTx} />
        </Fact>
      </dl>
      {campaign.voidCall && (
        <>
          <VoidDeposit campaign={campaign} owner={campaign.voidCall.from} />
          <div className="mt-4 space-y-3 text-sm">
            <CopyField label="To" value={campaign.voidCall.to} />
            <CopyField label="Data" value={campaign.voidCall.data} />
          </div>
        </>
      )}
    </li>
  );
}

/**
 * Voids a deposit from the owner wallet, which the admin must be signed in with. The budget leaves
 * the escrow for good, so the page asks first.
 */
function VoidDeposit({ campaign, owner }: { campaign: AdminCampaign; owner: string }) {
  const voidDeposit = useVoidDeposit();
  const { data: session } = useSession();
  const { isConnected } = useConnection();
  const [confirming, setConfirming] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const isOwner = session?.wallet === owner.toLowerCase();

  if (voidDeposit.data) {
    return (
      <p className="mt-4 flex flex-wrap items-center gap-2 text-sm">
        <Badge tone="green">Sent</Badge>
        <TransactionLink chainId={campaign.chainId} hash={voidDeposit.data} />
        <span className="text-zinc-600 dark:text-zinc-400">
          The deposit leaves this list once the worker reads the escrow's event.
        </span>
      </p>
    );
  }
  return (
    <div className="mt-4 text-sm">
      {isOwner ? (
        <>
          <button
            type="button"
            disabled={voidDeposit.isPending}
            onClick={() => (isConnected ? setConfirming(true) : setSigningIn(true))}
            className={styles.dangerButton}
          >
            {voidDeposit.isPending ? "Voiding…" : "Void deposit"}
          </button>
          {!isConnected && (
            <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
              Your wallet is not connected, so voiding signs you in with it again first.
            </p>
          )}
        </>
      ) : (
        <p className="text-zinc-600 dark:text-zinc-400">
          Sign in with the owner wallet, <AddressLink chainId={campaign.chainId} address={owner} />,
          to void it.
        </p>
      )}
      <ConfirmDialog
        open={confirming}
        title={`Void campaign ${campaign.id}?`}
        confirmLabel="Void deposit"
        cancelLabel="Keep the deposit"
        onConfirm={() => voidDeposit.mutate(campaign)}
        onClose={() => setConfirming(false)}
      >
        <p>
          Its whole budget, {formatAmount(campaign.budget, campaign.token)}, goes back to its brand.
          Voiding cannot be undone.
        </p>
      </ConfirmDialog>
      <SignInDialog open={signingIn} onClose={() => setSigningIn(false)} />
      {voidDeposit.isError && (
        <Notice tone="error" className="mt-3">
          {describeEscrowError(voidDeposit.error)}
        </Notice>
      )}
    </div>
  );
}

/** A value to copy into another tool, with a button that copies it. */
function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Without clipboard access, the value can still be selected and copied by hand.
    }
  }
  return (
    <div>
      <div className="flex items-center gap-3">
        <span className="font-medium">{label}</span>
        <button type="button" onClick={copy} className={styles.secondaryButton}>
          {copied ? "Copied" : `Copy ${label.toLowerCase()}`}
        </button>
      </div>
      <code className="mt-1 block font-mono text-xs break-all">{value}</code>
    </div>
  );
}

/** Every campaign, with the request it funds. */
function CampaignTable({ campaigns }: { campaigns: AdminCampaign[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="text-zinc-500 dark:text-zinc-400">
          <tr>
            <th scope="col" className="py-2 pr-4 font-medium">
              Campaign
            </th>
            <th scope="col" className="py-2 pr-4 font-medium">
              Request
            </th>
            <th scope="col" className="py-2 pr-4 font-medium">
              Status
            </th>
            <th scope="col" className="py-2 pr-4 font-medium">
              Budget
            </th>
            <th scope="col" className="py-2 pr-4 font-medium">
              Start
            </th>
            <th scope="col" className="py-2 font-medium">
              Deposited
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-200 dark:divide-white/10">
          {campaigns.map((campaign) => (
            <tr key={`${campaign.escrow}-${campaign.id}`}>
              <td className="py-2 pr-4">
                {campaign.id}{" "}
                <span className="font-mono text-xs text-zinc-500 dark:text-zinc-400">
                  {shortHex(campaign.escrow)}
                </span>
              </td>
              <td className="py-2 pr-4">
                {campaign.requestId === null ? (
                  <span className="text-zinc-500 dark:text-zinc-400">None</span>
                ) : (
                  <Link
                    to={`/admin/campaign-requests/${campaign.requestId}`}
                    className="hover:underline"
                  >
                    Request {campaign.requestId}
                  </Link>
                )}
              </td>
              <td className="py-2 pr-4">
                <CampaignStatusBadge status={campaign.status} />
              </td>
              <td className="py-2 pr-4">{formatAmount(campaign.budget, campaign.token)}</td>
              <td className="py-2 pr-4">{formatUtc(campaign.startAt)}</td>
              <td className="py-2">{formatUtc(campaign.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const CAMPAIGN_STATUS: Record<
  Campaign["status"],
  { label: string; tone: "green" | "grey" | "blue" | "amber" }
> = {
  funded: { label: "Funded", tone: "green" },
  cancelled: { label: "Cancelled", tone: "grey" },
  settled: { label: "Settled", tone: "blue" },
  refunded: { label: "Refunded", tone: "amber" },
};

function CampaignStatusBadge({ status }: { status: Campaign["status"] }) {
  const { label, tone } = CAMPAIGN_STATUS[status];
  return <Badge tone={tone}>{label}</Badge>;
}
